// LA COULEUR DES CATÉGORIES.
//
// La couleur sert ici à RECONNAÎTRE, pas à décorer : un même tag, un même
// client, un même type d'étude porte la même teinte partout dans l'application.
// Les teintes sont définies dans `styles/tokens.css` (`--hue-*`) ; ce module dit
// seulement laquelle va à quoi.
import type { IconName } from '../components/icons';
import type { ProblemType, RecoKind, StudyKind } from '../api/types';

export type Hue = 'cyan' | 'orange' | 'red' | 'amber' | 'green' | 'violet' | 'pink' | 'blue';

// L'ordre mélange volontairement chaud et froid : deux libellés voisins dans
// l'alphabet ne tombent pas sur deux teintes voisines.
const WHEEL: Hue[] = ['orange', 'violet', 'green', 'pink', 'blue', 'amber', 'cyan', 'red'];

/**
 * Teinte STABLE d'un libellé libre (tag, client, catégorie d'audit). Insensible
 * à la casse et aux accents : « Hors-ligne » et « hors ligne » restent de la
 * même couleur.
 */
export function hueOf(label: string): Hue {
  const key = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '');
  // FNV-1a : court, et il répartit mieux les mots courts sur huit teintes
  // qu'un simple cumul (qui regroupait la moitié des tags sur deux couleurs).
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return WHEEL[h % WHEEL.length]!;
}

/**
 * Teintes d'une LISTE courte (les clients du portefeuille) : attribuées dans
 * l'ordre, donc toutes différentes tant qu'il y en a huit ou moins — ce qu'un
 * tirage par libellé ne garantit pas.
 */
export function huesInOrder(labels: string[]): Map<string, Hue> {
  const out = new Map<string, Hue>();
  for (const label of labels) {
    if (!out.has(label)) out.set(label, WHEEL[out.size % WHEEL.length]!);
  }
  return out;
}

/** Classes à poser sur une pastille pour la colorer. */
export const hueClass = (hue: Hue) => `hue hue-${hue}`;

// --- Catégories FERMÉES : une teinte choisie, pas tirée au hasard -------------

/** La sévérité se lit à la couleur : ce qui bloque est rouge, ce qui aide est vert. */
export const SEVERITY_HUE: Record<string, Hue> = { Bloquant: 'red', Plafond: 'orange', Levier: 'green' };

export const PROBLEM_HUE: Record<ProblemType, Hue> = { irritant: 'orange', besoin: 'blue', contournement: 'amber' };

/** Une priorité basse reste neutre : ce n'est ni une alerte ni une bonne nouvelle. */
export const PRIORITY_HUE: Record<'haute' | 'moyenne' | 'basse', Hue | null> = { haute: 'red', moyenne: 'amber', basse: null };

export const RECO_HUE: Record<RecoKind, Hue> = {
  interface: 'violet',
  fonctionnalite: 'blue',
  processus: 'amber',
  contenu: 'pink',
  ia_data: 'green',
  autre: 'cyan',
};

export const KIND_STYLE: Record<StudyKind, { hue: Hue; icon: IconName }> = {
  entretien: { hue: 'orange', icon: 'message' },
  observation: { hue: 'green', icon: 'eye' },
  test: { hue: 'violet', icon: 'click' },
  questionnaire: { hue: 'blue', icon: 'checklist' },
  audit: { hue: 'pink', icon: 'audit' },
};

// --- STATUTS : où en est une chose ------------------------------------------
//
// Les statuts sont du texte libre (« Planifiée », « MVP livré », « Cadrage »…) :
// on les range par mots-clés, sans casse ni accents. Un statut inconnu reste
// neutre — mieux vaut pas de couleur qu'une couleur qui ment.
const STATUS_RULES: [RegExp, Hue][] = [
  [/bloqu|echec|refus|abandon|annul|retard/, 'red'],
  [/planifi|prevu|prevoir|a venir/, 'green'],
  [/a analyser|a completer|a preparer|a ecrire|a faire|en attente/, 'amber'],
  [/en cours|encours|en production|pilote|recrutement|terrain/, 'orange'],
  [/termin|livre|clotur|deploy|fini|resolu|traite|valide/, 'blue'],
  [/cadrage|brouillon|preparation|nouveau/, 'violet'],
];

export function statusHue(value: string): Hue | null {
  const key = value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  for (const [re, hue] of STATUS_RULES) if (re.test(key)) return hue;
  return null;
}
